import { useState } from 'react';
import {
  PurchaseSummary,
  useBreakpoint
} from '@cencosud-cencommerce/paris-uikit';

import { useAppSelector } from '@/Application/Hooks/Redux';
import {
  cartToCartData,
  deliveryInfoToShippingData
} from '@/modules/cart/infrastructure/mappers/cartToCartData';
import { CouponComponent } from '@/modules/perseo-billing/components/coupon-component';
import {
  useEditCart,
  useGetCouponCartData,
  useMutationStatus
} from '@/modules/perseo-billing/containers/billing/Summary/hooks';
import { useBodyScrollLock } from '@/modules/perseo-billing/containers/billing/Summary/hooks/use-body-scroll-lock';
import {
  DeliveryTypes,
  SkeletonDelivery,
  SkeletonSummary
} from '@/modules/perseo-billing/containers/order-summary';
import {
  useGetCartTotals,
  useGetCustomerData,
  useGiftCardData,
  usePayment
} from '@/modules/perseo-billing/hooks';
import { isDeviceAndroid, isDeviceIos } from '@/modules/perseo-billing/utils';
import { isWeddingFlow } from '@/modules/shared/utils/isWeddingFlow';
import { useGetCostsOnMobile } from '@/sections/cart/hooks/useGetCostsOnMobile';

const isWedding = isWeddingFlow();
const isWebView = isDeviceAndroid() || isDeviceIos();

export function SummaryContainer() {
  const [isOpen, setIsOpen] = useState(false);

  const { isMobile, isTablet } = useBreakpoint();
  const isMobileOrTablet = isMobile || isTablet;
  const billing = useAppSelector((state) => state.billing);
  const { data: cart, isLoading } = useAppSelector((state) => state.cart);

  const { getFormattedGiftCardData } = useGiftCardData();
  const giftCardData = getFormattedGiftCardData();
  const couponData = useGetCouponCartData();
  const cartTotals = useGetCartTotals();
  const customer = useGetCustomerData();
  const { getCostsOnMobile } = useGetCostsOnMobile();

  const { onEditCart } = useEditCart('summary');
  const { handleSubmitForm } = usePayment();

  const isMutating = useMutationStatus();

  useBodyScrollLock(isOpen);

  const totals = cartTotals;
  const { otherMethods, cencosudCard, cencoPay } = totals;

  if (!cart?.id || isLoading) {
    return (
      <>
        <SkeletonSummary />
        <SkeletonDelivery />
      </>
    );
  }

  const showEditButton = !isWebView && !isWedding;
  const { isPaymentAvailable } = billing;
  const showCostsOnMobile = getCostsOnMobile();
  const shippingData = isWedding
    ? undefined
    : deliveryInfoToShippingData(cart?.deliveryInfo);
  const newCartData = cartToCartData(cart, customer);
  const cpayPrice = cencoPay?.price ?? otherMethods?.price;
  const showTotalPriceCencoPay = isMobileOrTablet && cpayPrice;
  const totalPriceCencoPay = showTotalPriceCencoPay ? cpayPrice : undefined;

  const purchaseSummaryClassName = isOpen
    ? 'co-z-10 lg:co-z-auto co-rounded-none'
    : 'co-z-10 lg:co-z-auto';

  const showGiftCardAppliedPrice = totals?.hasGiftCardApplied;

  const totalPrice = showGiftCardAppliedPrice
    ? otherMethods?.withGiftCard?.price
    : otherMethods?.price;

  const totalPriceCenco = showGiftCardAppliedPrice
    ? otherMethods?.withGiftCard?.price
    : cencosudCard?.price;

  const toggleIsOpen = () => {
    setIsOpen(!isOpen);
  };

  const onContinue = () => {
    handleSubmitForm();
  };

  return (
    <>
      <PurchaseSummary
        data-testid="paris-purchase-summary"
        className={purchaseSummaryClassName}
        isOpen={isOpen}
        onOpen={toggleIsOpen}
        showCostsOnMobile={showCostsOnMobile}
        continueButtonDisabled={!isPaymentAvailable || isMutating}
        onContinue={isMobileOrTablet ? onContinue : undefined}
        isVisibleEditButton={showEditButton}
        onEditCart={onEditCart}
        couponData={couponData}
        CouponComponent={CouponComponent}
        giftCardData={giftCardData}
        cartData={newCartData}
        shippingData={shippingData}
        totalPrice={totalPrice}
        totalPriceCenco={totalPriceCenco}
        totalPriceCencoPay={totalPriceCencoPay}
      />
      {!isMobileOrTablet && <DeliveryTypes />}
    </>
  );
}
